import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { ICategory } from '../../types/categories'
import { useLayout } from '../../context/LayoutContext'
import { haptic } from '../../utils/fx'
import CategoryCountdown from './CategoryCountdown'
import { categoryIcon } from './categoryIcon'
import './CategoriesSlider.scss'

type Props = {
  data: ICategory[]
  available: Set<string>
  showSearch: boolean
}

const c = 'category-chips'
const SPY_OFFSET = 150

const CategoriesSlider = ({ data, available, showSearch }: Props) => {
  const { t, i18n } = useTranslation()
  const { searchValue, setSearchValue, searchOpen, setSearchOpen } = useLayout()
  const [active, setActive] = useState<string | null>(null)
  const [stuck, setStuck] = useState(false)
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })
  const [canScroll, setCanScroll] = useState({ left: false, right: false })
  const trackRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const lockUntil = useRef(0)

  const name = (item: ICategory) =>
    i18n.language === 'ru' && item?.ru_name ? item.ru_name : item.name

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const io = new IntersectionObserver(([entry]) =>
      setStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0),
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    const onScroll = () => {
      if (Date.now() < lockUntil.current) return
      let current: string | null = null
      for (const item of data) {
        const section = document.getElementById(`category-${item._id}`)
        if (section && section.getBoundingClientRect().top - SPY_OFFSET <= 0) {
          current = item._id
        }
      }
      setActive(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [data, available])

  useLayoutEffect(() => {
    const track = trackRef.current
    const chip = track?.querySelector<HTMLElement>(`[data-id="${active}"]`)
    if (!track || !chip) {
      setIndicator((prev) => ({ ...prev, width: 0 }))
      return
    }
    setIndicator({ left: chip.offsetLeft, width: chip.offsetWidth })
    track.scrollTo({
      left: chip.offsetLeft - track.clientWidth / 2 + chip.offsetWidth / 2,
      behavior: 'smooth',
    })
  }, [active, i18n.language, data])

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus({ preventScroll: true })
  }, [searchOpen])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const update = () => {
      const max = track.scrollWidth - track.clientWidth
      setCanScroll((prev) => {
        const next = { left: track.scrollLeft > 4, right: track.scrollLeft < max - 4 }
        return prev.left === next.left && prev.right === next.right ? prev : next
      })
    }
    update()
    track.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      track.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [data, i18n.language])

  function scrollTrack(direction: 1 | -1) {
    const track = trackRef.current
    if (!track) return
    track.scrollBy({ left: direction * track.clientWidth * 0.7, behavior: 'smooth' })
  }

  function handleClick(id: string) {
    const section = document.getElementById(`category-${id}`)
    if (!section) return
    haptic()
    lockUntil.current = Date.now() + 900
    setActive(id)
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function closeSearch() {
    setSearchValue('')
    setSearchOpen(false)
  }

  return (
    <>
      <div ref={sentinelRef} className={`${c}__sentinel`} id='menu-top' />
      <div
        className={`${c}${stuck ? ' is-stuck' : ''}${searchOpen ? ' is-searching' : ''}`}
      >
        <div className={`${c}__inner`}>
          {showSearch && (
            <div className={`${c}__search`}>
              <button
                className={`${c}__search-btn`}
                onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
                aria-label={searchOpen ? t('CLEAR_SEARCH') : t('SEARCH')}
              >
                <FontAwesomeIcon icon={searchOpen ? faXmark : faMagnifyingGlass} />
              </button>
              <input
                ref={inputRef}
                className={`${c}__search-input`}
                value={searchValue}
                placeholder={t('SEARCH')}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                tabIndex={searchOpen ? 0 : -1}
                enterKeyHint='search'
                aria-label='search'
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur()
                  if (e.key === 'Escape') closeSearch()
                }}
              />
            </div>
          )}
          <div ref={trackRef} className={`${c}__track`}>
            <span
              className={`${c}__indicator`}
              style={{
                transform: `translateX(${indicator.left}px)`,
                width: indicator.width,
                opacity: indicator.width ? 1 : 0,
              }}
            />
            {data.map((item) => {
              return (
                <button
                  key={item._id}
                  data-id={item._id}
                  className={`${c}__chip${active === item._id ? ' is-active' : ''}${available.has(item._id) ? '' : ' is-empty'}`}
                  onClick={() => handleClick(item._id)}
                >
                  <FontAwesomeIcon icon={categoryIcon(item.name)} className={`${c}__icon`} />
                  {name(item)}
                  {item.isNew && <span className={`${c}__new`}>{t('NEW')}</span>}
                  {item.specialOfferEnd && (
                    <CategoryCountdown
                      endDate={item.specialOfferEnd}
                      className={`${c}__countdown`}
                    />
                  )}
                </button>
              )
            })}
          </div>
          {canScroll.left && (
            <button
              className={`${c}__arrow ${c}__arrow--left`}
              onClick={() => scrollTrack(-1)}
              aria-label='previous'
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}
          {canScroll.right && (
            <button
              className={`${c}__arrow ${c}__arrow--right`}
              onClick={() => scrollTrack(1)}
              aria-label='next'
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default CategoriesSlider
